/**
 * useBreakpoint — détecte le breakpoint actif (mobile / tablet / desktop).
 *
 * @remarks
 * Pilote la bascule entre `DesktopShell`, `TabletShell` et `MobileShell`
 * dans `AppShell.tsx`.
 *
 * Breakpoints :
 * - **mobile** : `< 768px`
 * - **tablet** : `768px - 1279px`
 * - **desktop** : `≥ 1280px`
 */

import { useEffect, useState } from 'react';

type Breakpoint = 'mobile' | 'tablet' | 'desktop';

const MOBILE_MAX = 767;
const TABLET_MAX = 1279;

function compute(width: number): Breakpoint {
  if (width <= MOBILE_MAX) return 'mobile';
  if (width <= TABLET_MAX) return 'tablet';
  return 'desktop';
}

export function useBreakpoint(): Breakpoint {
  const [bp, setBp] = useState<Breakpoint>(() => {
    if (typeof window === 'undefined') return 'desktop';
    return compute(window.innerWidth);
  });

  useEffect(() => {
    const onResize = () => {
      setBp(compute(window.innerWidth));
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  return bp;
}
